import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  Modal,
  Autocomplete,
  Divider,
  Stack,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "95%", md: 720 },
  bgcolor: "background.paper",
  borderRadius: 2,
  boxShadow: 24,
  overflow: "hidden",
};

function CreateLocationModal({ open, handleClose }) {
  const { t } = useTranslation(["Location"]);
  const userState = useSelector((state) => state.user);

  // Champs
  const [locationName, setLocationName] = useState("");
  const [adresse, setAdresse] = useState("");
  const [ville, setVille] = useState("");
  const [codePostal, setCodePostal] = useState("");

  // Localisation
  const [countries, setCountries] = useState([]);
  const [country, setCountry] = useState(null);
  const [province, setProvince] = useState(null);
  const [region, setRegion] = useState(null);
  const [availableProvinces, setAvailableProvinces] = useState([]);
  const [availableRegions, setAvailableRegions] = useState([]);

  // Reset
  useEffect(() => {
    if (!open) {
      setLocationName("");
      setAdresse("");
      setVille("");
      setCodePostal("");
      setCountry(null);
      setProvince(null);
      setRegion(null);
    }
  }, [open]);

  // Fetch pays
  useEffect(() => {
    if (!open) return;

    fetch(`${process.env.REACT_APP_API_URL}/Localization/GetCountries`, {
      headers: { Authorization: `Bearer ${userState.token}` },
    })
      .then((r) => r.json())
      .then(setCountries)
      .catch(console.error);
  }, [open]);

  // Provinces
  useEffect(() => {
    if (!country) return;

    fetch(
      `${process.env.REACT_APP_API_URL}/Localization/GetProvinceByCountry/${country.noPays}`,
      { headers: { Authorization: `Bearer ${userState.token}` } }
    )
      .then((r) => r.json())
      .then((d) => {
        setAvailableProvinces(d);
        setProvince(null);
        setRegion(null);
      });
  }, [country]);

  // Regions
  useEffect(() => {
    if (!province) return;

    fetch(
      `${process.env.REACT_APP_API_URL}/Localization/GetRegionsByProvinces/${province.noProvince}`,
      { headers: { Authorization: `Bearer ${userState.token}` } }
    )
      .then((r) => r.json())
      .then(setAvailableRegions);
  }, [province]);

  const handleCreate = async () => {
    const payload = {
      Nom: locationName,
      Adresse: adresse,
      City: ville,
      PostalCode: codePostal,
      Country: country,
      Province: province,
      Region: region,
    };

    await fetch(`${process.env.REACT_APP_API_URL}/Location/CreateLocation`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${userState.token}`,
      },
      body: JSON.stringify(payload),
    });

    handleClose();
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box sx={modalStyle}>
        {/* HEADER */}
        <Box sx={{ px: 4, py: 3 }}>
          <Typography variant="h6" fontWeight={600}>
            {t("CreateLocationTitle")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t("FillLocationInformation")}
          </Typography>
        </Box>

        <Divider />

        {/* CONTENT */}
        <Box sx={{ px: 4, py: 3 }}>
          <Grid container spacing={2.5}>
            <Grid item xs={12}>
              <TextField
                label={t("LocationName")}
                fullWidth
                size="small"
                value={locationName}
                onChange={(e) => setLocationName(e.target.value)}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label={t("Adress")}
                fullWidth
                size="small"
                value={adresse}
                onChange={(e) => setAdresse(e.target.value)}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                label={t("City")}
                fullWidth
                size="small"
                value={ville}
                onChange={(e) => setVille(e.target.value)}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                label={t("Zipcode")}
                fullWidth
                size="small"
                value={codePostal}
                onChange={(e) => setCodePostal(e.target.value)}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
            </Grid>

            <Grid item xs={4}>
              <Autocomplete
                options={countries}
                value={country}
                getOptionLabel={(o) => o?.nomFr ?? ""}
                onChange={(_, v) => setCountry(v)}
                renderInput={(params) => (
                  <TextField {...params} label={t("Country")} size="small" />
                )}
              />
            </Grid>

            <Grid item xs={4}>
              <Autocomplete
                options={availableProvinces}
                value={province}
                disabled={!country}
                getOptionLabel={(o) => o?.nomFr ?? ""}
                onChange={(_, v) => setProvince(v)}
                renderInput={(params) => (
                  <TextField {...params} label={t("Province")} size="small" />
                )}
              />
            </Grid>

            <Grid item xs={4}>
              <Autocomplete
                options={availableRegions}
                value={region}
                disabled={!province}
                getOptionLabel={(o) => o?.nomFr ?? ""}
                onChange={(_, v) => setRegion(v)}
                renderInput={(params) => (
                  <TextField {...params} label={t("Region")} size="small" />
                )}
              />
            </Grid>
          </Grid>
        </Box>

        <Divider />

        {/* FOOTER */}
        <Stack
          direction="row"
          spacing={2}
          justifyContent="flex-end"
          sx={{ px: 4, py: 2 }}
        >
          <Button onClick={handleClose} color="inherit">
            {t("Cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={!locationName}
          >
            {t("Create")}
          </Button>
        </Stack>
      </Box>
    </Modal>
  );
}

export default CreateLocationModal;
